import { useEffect, useState, useCallback } from 'react';
import { WebApiModelsMemberField, WebApiModelsMembershipProduct, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto } from '../../Repository/eventbookyapi';
import MemberList from '../../Components/Users/MemberList';
import { useAuth } from '../../Contexts/AuthContext';
import { TextService } from '../../Services/TextService';
import LeftMenu from './LeftMenu';
import { MemberService } from '../../Services/MemberService';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import { MemberFieldService } from '../../Services/MemberFieldService';
import LoadingSpinner from '../../Components/Shared/LoadingSpinner';
import { MembershipProductService } from '../../Services/MembershipProductService';
import { AllowCreateInvoiceOption } from '../../Types/AllowCreateInvoiceOption';

const AdminUsers = () => {
  const [memberResult, setMemberResult] = useState({} as WebApiServicesDataTransferObjectsGroupServiceGroupResultDto);
  const [memberFieldSettings, setMemberFieldSettings] = useState([] as WebApiModelsMemberField[]);
  const [membershipProducts, setMembershipProducts] = useState([] as WebApiModelsMembershipProduct[]);
  const [hasPingPaymentIntegration, setHasPingPaymentIntegration] = useState(false as boolean);
  const [hasFortnoxIntegration, setHasFortnoxIntegration] = useState(false as boolean);
  const [hasSpirisIntegration, setHasSpirisIntegration] = useState(false as boolean);
  const [allowCreateFortnoxInvoice, setAllowCreateFortnoxInvoice] = useState(false as boolean);
  const [allowCreateSpirisInvoice, setAllowCreateSpirisInvoice] = useState(false as boolean);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const GetText = TextService.GetText;

  // Single useEffect to fetch all data - eliminates duplicate API calls
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [members, fields, integrations, membershipProducts] = await Promise.all([
          MemberService.GetAllMembers(),
          MemberFieldService.GetMemberFields(),
          IntegrationService.GetActiveIntegrations(),
          MembershipProductService.GetAllMembershipProducts()
        ]);

        setMemberResult(members);
        setMemberFieldSettings(fields);
        setMembershipProducts(membershipProducts);
        const pingPayments = integrations.find((p: any) => p.type == IntegrationType.PingPayments);
        setHasPingPaymentIntegration(!!pingPayments);
        const fortnox = integrations.find((p: any) => p.type == IntegrationType.Fortnox);
        setHasFortnoxIntegration(!!fortnox);
        setAllowCreateFortnoxInvoice(fortnox?.allowCreateInvoice === AllowCreateInvoiceOption.Allowed);
        const spiris = integrations.find((p: any) => p.type == IntegrationType.Spiris);
        setHasSpirisIntegration(!!spiris);
        setAllowCreateSpirisInvoice(spiris?.allowCreateInvoice === AllowCreateInvoiceOption.Allowed);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Super simple: just check if we have the basic data we need.
  // user must be loaded too — MemberList reads props.user.* unguarded and the
  // auth context resolves independently of the member Promise.all above.
  const hasData = memberResult.members != null && user != null;

  // Memoize the loadMembers function to prevent unnecessary re-creations
  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      // Only reload members and fields, not user/integrations
      const [members, fields] = await Promise.all([
        MemberService.GetAllMembers(),
        MemberFieldService.GetMemberFields()
      ]);
      setMemberResult(members);
      setMemberFieldSettings(fields);
    } catch (error) {
      console.error('Error reloading members:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  return <LayoutLeftMenu title={GetText("Users_UsersTitle")} menu={<LeftMenu selected={"users"}></LeftMenu>}>
    {hasData ? (
      <MemberList
        user={user}
        memberResult={memberResult}
        hasPingPaymentIntegration={hasPingPaymentIntegration}
        hasFortnoxIntegration={hasFortnoxIntegration}
        hasSpirisIntegration={hasSpirisIntegration}
        allowCreateFortnoxInvoice={allowCreateFortnoxInvoice}
        allowCreateSpirisInvoice={allowCreateSpirisInvoice}
        membershipProducts={membershipProducts}
        loadMembers={loadMembers}
        memberFieldSettings={memberFieldSettings}
      />
    ) : (
      <LoadingSpinner loadingText="Laddar medlemmar..." />
    )}
  </LayoutLeftMenu>
};

export default AdminUsers;
